import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "utils/api";
import { formatLong, statusLabel, translateServiceLabel } from "./formatters";
import { useTeamLocale } from "./TeamLocaleContext";

const POLL_MS = 20_000;

const TeamAdminLiveContext = createContext(null);

const buildNotificationMessage = (t, dateLocale, change) => {
  const b = change.booking;
  const service = translateServiceLabel(t, b);
  const when = formatLong(b.start, dateLocale);
  const name = [b.customer?.firstName, b.customer?.lastName].filter(Boolean).join(" ");
  const status = statusLabel(t, b.status);

  if (change.action === "created") {
    return t("dashboard.notification.newBooking", {
      service,
      name: name || b.customer?.email || "—",
      when,
      status,
    });
  }
  return t("dashboard.notification.updatedBooking", {
    service,
    name: name || b.customer?.email || "—",
    when,
    status,
  });
};

export const TeamAdminLiveProvider = ({
  children,
  buildActivityParams,
  onNavigate,
}) => {
  const { t, dateLocale } = useTeamLocale();
  const [refreshTick, setRefreshTick] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const sinceRef = useRef(null);
  const bootstrappedRef = useRef(false);

  const poll = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const params = buildActivityParams(
        sinceRef.current ? { since: sinceRef.current } : {}
      );
      const { data } = await api.get("/admin/activity", { params });
      sinceRef.current = data.checkedAt;

      if (!bootstrappedRef.current) {
        bootstrappedRef.current = true;
        return;
      }

      const changes = data.changes || [];
      if (changes.length === 0) return;

      setNotifications((prev) => {
        const seen = new Set(prev.map((n) => n.id));
        const incoming = changes
          .map((change) => ({
            id: `${change.type}-${change.booking._id}-${change.action}`,
            type: change.type,
            action: change.action,
            booking: change.booking,
            message: buildNotificationMessage(t, dateLocale, change),
          }))
          .filter((n) => !seen.has(n.id));
        return [...incoming, ...prev].slice(0, 6);
      });
      setRefreshTick((n) => n + 1);
    } catch {
      /* ignore transient poll errors */
    }
  }, [buildActivityParams, t, dateLocale]);

  useEffect(() => {
    bootstrappedRef.current = false;
    sinceRef.current = null;
    poll();
    const id = window.setInterval(poll, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [poll]);

  const dismissNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const openNotification = useCallback(
    (notification) => {
      dismissNotification(notification.id);
      onNavigate?.("calendar", notification.booking);
    },
    [dismissNotification, onNavigate]
  );

  const value = useMemo(
    () => ({
      refreshTick,
      notifications,
      dismissNotification,
      clearNotifications,
      openNotification,
    }),
    [
      refreshTick,
      notifications,
      dismissNotification,
      clearNotifications,
      openNotification,
    ]
  );

  return (
    <TeamAdminLiveContext.Provider value={value}>
      {children}
    </TeamAdminLiveContext.Provider>
  );
};

export const useTeamAdminLive = () => {
  const ctx = useContext(TeamAdminLiveContext);
  if (!ctx) {
    throw new Error("useTeamAdminLive must be used within TeamAdminLiveProvider");
  }
  return ctx;
};

export const useTeamAdminLiveOptional = () => useContext(TeamAdminLiveContext);
